import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Offcanvas } from "bootstrap";
import SearchBar from "../SearchBar/searchBar";
import CLINIC_CONFIG from '../../../config/clinicConfig';
import { trackMembershipClick } from '../../../utils/gtag';

import { locationSuffix } from '../../../config/routes';

interface ArrowIconProps {
    isOpen: boolean;
}

const ArrowIcon: React.FC<ArrowIconProps> = ({ isOpen }) => (
    <span
        style={{
            display: "inline-block",
            marginLeft: "auto",
            transition: "transform 0.3s ease",
            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
        }}
    >
        ▶
    </span>
);

type MenuKeys =
    | "about"
    | "services"
    | "routineCheckup"
    | "generalDentistry"
    | "fillings"
    | "preventive"
    | "endodontics"
    | "prosthodontics"
    | "pediatric"
    | "insurance"
    | "financing";

type OpenMenusState = {
    [key in MenuKeys]?: boolean;
};

interface MobileOffcanvasNavProps {
    routes: string[];
}

const MobileOffcanvasNav: React.FC<MobileOffcanvasNavProps> = ({ routes }) => {
    const [openMenus, setOpenMenus] = useState<OpenMenusState>({});
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const location = useLocation();

    const closeOffcanvas = () => {
        const el = document.getElementById("offcanvasNav");
        if (!el) return;
        const inst = Offcanvas.getInstance(el);
        if (inst) {
            inst.hide();
        } else {
            el.classList.remove("show");
            el.setAttribute("aria-hidden", "true");
            document.querySelector(".offcanvas-backdrop")?.remove();
            document.body.classList.remove("offcanvas-open");
            document.body.style.removeProperty("overflow");
        }
        setOpenMenus({});
        setIsOpen(false);
    };

    useEffect(() => {
        // Auto-close offcanvas when route changes
        closeOffcanvas();
    }, [location]);

    const toggleMenu = (menu: MenuKeys) => {
        setOpenMenus((prev) => ({
            ...prev,
            [menu]: !prev[menu],
        }));
    };
    const handleOffcanvasBodyClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a');
        if (!anchor) return;
        closeOffcanvas();
    };
    return (
        <nav className="navbar navbar-light bg-light py-3 d-lg-none">
            <div className="container-fluid">
                <Link className="navbar-brand" to="/">
                    <img
                        src="/logo.png"
                        alt={`${CLINIC_CONFIG.clinicName} Logo`}
                        height={50}
                        width={150}
                    />
                </Link>
                <div className='d-flex justify-content-end align-items-center gap-2'>
                    <button
                        className="navbar-toggler"
                        type="button"
                        data-bs-toggle="offcanvas"
                        data-bs-target="#offcanvasNav"
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="position-relative">
                        {/* Search Button (when closed) */}
                        {!isOpen && (
                            <button
                                type="button"
                                className="btn d-flex align-items-center justify-content-center "
                                style={{
                                    backgroundColor: "lightgray",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: "40px",
                                    height: "40px",
                                    zIndex: 1051,
                                }}
                                onClick={() => setIsOpen(true)}
                            >
                                <i className="bi bi-search"></i>
                            </button>
                        )}
                        {/* Expanding Search Box */}
                        {isOpen && (
                            <div
                                className="d-flex align-items-center bg-white shadow rounded px-2 slide-in mobile-search-box"
                                style={{
                                    zIndex: 1051,
                                }}
                            >
                                <SearchBar routes={routes} />
                                <i
                                    className="bi bi-search ms-2 text-black"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setIsOpen(false)}
                                ></i>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                className="offcanvas offcanvas-start"
                id="offcanvasNav"
                tabIndex={-1}
                aria-labelledby="offcanvasLabel"
                data-bs-backdrop="false"
                data-bs-scroll="true"
            >
                <div className="offcanvas-header">
                    <h5 className="offcanvas-title" id="offcanvasLabel">Menu</h5>
                    <button
                        type="button"
                        className="btn-close text-reset"
                        data-bs-dismiss="offcanvas"
                        aria-label="Close"
                    ></button>
                </div>
                <div className="offcanvas-body" onClickCapture={handleOffcanvasBodyClick}>
                    <ul className="navbar-nav flex-column">
                        {/* About Us */}
                        <li className="nav-item">
                            <Link className="nav-link" to="/about-us">
                                About Us
                            </Link>
                        </li>

                        <li className="nav-item">
                            <Link className="nav-link" to="/blog">
                                Blog
                            </Link>
                        </li>
                        {/* Services */}
                        <li className="nav-item">
                            <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("services")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.services}>
                                Services <ArrowIcon isOpen={!!openMenus.services} />
                            </div>
                            {openMenus.services && (
                                <ul className="ps-3 list-unstyled">
                                    <li> <Link className="nav-link" to="/services">
                                        Services
                                    </Link></li>
                                    {/* Routine Tooth Checkup */}
                                    <li>

                                        <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("routineCheckup")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.routineCheckup}>
                                            Routine Tooth Checkup <ArrowIcon isOpen={!!openMenus.routineCheckup} />
                                        </div>
                                        {openMenus.routineCheckup && (
                                            <ul className="ps-3 list-unstyled">
                                                <li>  <Link className="nav-link" to={`/routine-tooth-checkup${locationSuffix}`}>
                                                    Routine Tooth Checkup
                                                </Link></li>
                                                <li><Link className="nav-link" to={`/missing-teeth${locationSuffix}`}>Missing Teeth</Link></li>
                                                <li><Link className="nav-link" to={`/tooth-infection${locationSuffix}`}>Tooth Infection</Link></li>
                                                <li><Link className="nav-link" to={`/tooth-decay${locationSuffix}`}>Tooth Decay</Link></li>
                                                <li><Link className="nav-link" to={`/weak-tooth${locationSuffix}`}>Weak Tooth</Link></li>
                                            </ul>
                                        )}
                                    </li>
                                    {/* General Dentistry */}
                                    <li>
                                        <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("generalDentistry")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.generalDentistry}>
                                            General Dentistry <ArrowIcon isOpen={!!openMenus.generalDentistry} />
                                        </div>
                                        {openMenus.generalDentistry && (
                                            <ul className="ps-3 list-unstyled">
                                                <li> <Link className="nav-link" to={`/general-dentistry${locationSuffix}`}>
                                                    General Dentistry
                                                </Link></li>
                                                <li><Link className="nav-link" to={`/dental-exams-and-cleanings${locationSuffix}`}>Dental Exams and Cleanings</Link></li>
                                                <li><Link className="nav-link" to={`/dental-cleaning-and-prevention${locationSuffix}`}>Dental Cleaning and Prevention</Link></li>

                                                {/* Fillings */}
                                                <li>
                                                    <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("fillings")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.fillings}>
                                                        Fillings <ArrowIcon isOpen={!!openMenus.fillings} />
                                                    </div>
                                                    {openMenus.fillings && (
                                                        <ul className="ps-3 list-unstyled">
                                                            <li> <Link className="nav-link" to={`/fillings${locationSuffix}`}>
                                                                Fillings
                                                            </Link></li>
                                                            <li><Link className="nav-link" to={`/glass-ionomer-fillings${locationSuffix}`}>Glass Ionomer</Link></li>
                                                            <li><Link className="nav-link" to={`/composite-fillings${locationSuffix}`}>Composite</Link></li>
                                                            <li><Link className="nav-link" to={`/amalgam-fillings${locationSuffix}`}>Amalgam</Link></li>
                                                        </ul>
                                                    )}
                                                </li>
                                                {/* Preventive */}
                                                <li>
                                                    <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("preventive")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.preventive}>
                                                        Preventive Dentistry <ArrowIcon isOpen={!!openMenus.preventive} />
                                                    </div>
                                                    {openMenus.preventive && (
                                                        <ul className="ps-3 list-unstyled">
                                                            <li><Link className="nav-link" to={`/preventive-dentistry${locationSuffix}`}>Preventive Dentistry</Link></li>
                                                            <li><Link className="nav-link" to={`/gingivitis${locationSuffix}`}>Gingivitis</Link></li>
                                                        </ul>
                                                    )}
                                                </li>
                                            </ul>
                                        )}
                                    </li>
                                    {/* Endodontics */}
                                    <li>
                                        <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("endodontics")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.endodontics}>
                                            Endodontics <ArrowIcon isOpen={!!openMenus.endodontics} />
                                        </div>
                                        {openMenus.endodontics && (
                                            <ul className="ps-3 list-unstyled">
                                                <li><Link className="nav-link" to={`/endodontics${locationSuffix}`}>Endodontics</Link></li>
                                                <li><Link className="nav-link" to={`/root-canal-treatment${locationSuffix}`}>Root Canal Treatment</Link></li>
                                                <li><Link className="nav-link" to={`/pulpotomies${locationSuffix}`}>Pulpotomies</Link></li>
                                            </ul>
                                        )}
                                    </li>
                                    {/* Prosthodontics */}
                                    <li>
                                        <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("prosthodontics")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.prosthodontics}>
                                            Prosthodontics <ArrowIcon isOpen={!!openMenus.prosthodontics} />
                                        </div>
                                        {openMenus.prosthodontics && (
                                            <ul className="ps-3 list-unstyled">
                                                <li><Link className="nav-link" to={`/prosthodontics${locationSuffix}`}>Prosthodontics </Link></li>
                                                <li><Link className="nav-link" to={`/dentures-partial-dentures${locationSuffix}`}>Dentures/Partial Dentures</Link></li>
                                                <li><Link className="nav-link" to={`/dental-bridges${locationSuffix}`}>Bridges</Link></li>
                                                <li><Link className="nav-link" to={`/inlay-onlay-restorations${locationSuffix}`}>Inlay & Onlay Restorations</Link></li>
                                                <li><Link className="nav-link" to={`/dental-implants${locationSuffix}`}>Dental Implants</Link></li>
                                            </ul>
                                        )}
                                    </li>
                                    {/* Pediatric */}
                                    <li>
                                        <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("pediatric")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.pediatric}>
                                            Pediatric Dentistry <ArrowIcon isOpen={!!openMenus.pediatric} />
                                        </div>
                                        {openMenus.pediatric && (
                                            <ul className="ps-3 list-unstyled">
                                                <li><Link className="nav-link" to={`/pediatric-dentistry${locationSuffix}`}>Pediatric Dentistry </Link></li>
                                                <li><Link className="nav-link" to={`/custom-sports-mouthguards${locationSuffix}`}>Custom Sports Mouthguards</Link></li>
                                                <li><Link className="nav-link" to={`/fluoride-trays${locationSuffix}`}>Fluoride Trays</Link></li>
                                            </ul>
                                        )}
                                    </li>
                                    {/* Additional */}
                                    <li><Link className="nav-link" to={`/restorative-dentistry${locationSuffix}`}>Restorative Dentistry</Link></li>
                                    <li><Link className="nav-link" to={`/cosmetic-dentistry`}>Cosmetic Dentistry</Link></li>
                                    <li><Link className="nav-link" to={`/periodontics${locationSuffix}`}>Periodontists</Link></li>
                                    <li><Link className="nav-link" to={`/orthodontics${locationSuffix}`}>Orthodontics</Link></li>
                                    <li><Link className="nav-link" to={`/emergency-services${locationSuffix}`}>Emergency Services</Link></li>
                                </ul>
                            )}
                        </li>
                        {/* Book Appointment */}
                        <li className="nav-item"><Link className="nav-link" to="/book-appointment">Book Appointment</Link></li>
                        {/* Patient Portal */}
                        <li className="nav-item"><Link className="nav-link" to="/patient-portal">Patient Portal</Link></li>
                        {/* Insurance */}
                        <li className="nav-item">
                            <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("insurance")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.insurance}>
                                Insurance <ArrowIcon isOpen={!!openMenus.insurance} />
                            </div>
                            {openMenus.insurance && (
                                <ul className="ps-3 list-unstyled">
                                    <li><Link className="nav-link" to="/insurance">Insurance </Link></li>
                                    <li><Link className="nav-link" to="/delta-dental">Delta Dental</Link></li>
                                    <li><Link className="nav-link" to="/blue-cross-blue-shield">Blue Cross Blue Shield</Link></li>
                                    <li><Link className="nav-link" to="/ppo">Connection Dental</Link></li>
                                </ul>
                            )}
                        </li>
                        {/* Medicaid */}
                        <li className="nav-item"><Link className="nav-link" to="/medicaid">Medicaid</Link></li>
                        {/* Medicare */}
                        <li className="nav-item"><Link className="nav-link" to="/medicare">Medicare</Link></li>
                        {/* Financing */}
                        <li className="nav-item">
                            <div className="nav-link d-flex align-items-center" onClick={() => toggleMenu("financing")} style={{ cursor: "pointer" }} aria-expanded={!!openMenus.financing}>
                                Financing <ArrowIcon isOpen={!!openMenus.financing} />
                            </div>
                            {openMenus.financing && (
                                <ul className="ps-3 list-unstyled">
                                    <li><Link className="nav-link" to="/financing">Financing </Link></li>
                                    <li><Link className="nav-link" to="/in-house-payment-plan">In House Payment Plan</Link></li>
                                    <li><Link className="nav-link" to="/cherry">Cherry</Link></li>
                                    <li><Link className="nav-link" to="/sunbit">Sunbit</Link></li>
                                    <li><a className="nav-link" href={CLINIC_CONFIG.membership} target="_blank" rel="noopener noreferrer" onClick={() => trackMembershipClick(CLINIC_CONFIG.membership)}>Kleer Membership</a></li>
                                    <li><Link className="nav-link" to="/care-credit">Care Credit</Link></li>
                                </ul>
                            )}
                        </li>
                        {/* Pay Your Bill */}
                        <li className="nav-item"><Link className="nav-link" to="/pay-your-bill">Pay Your Bill</Link></li>
                        {/* Contact Us */}
                        <li className="nav-item"><Link className="nav-link" to="/contact-us">Contact Us</Link></li>


                    </ul>
                </div>
            </div>
        </nav>
    );
};

export default MobileOffcanvasNav;
